import "../styles/globals.css";
import "../i18n";
import "regenerator-runtime/runtime";
import { GoogleTagManager } from "@next/third-parties/google";
import { PrismicPreview } from "@prismicio/next";

import { FeatureFlagProvider } from "@/context/FeatureFlagProvider";
import { repositoryName } from "@/prismic";
import type { NextPage } from "next";
import type { AppProps } from "next/app";
import Head from "next/head";
import type { ReactElement, ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { appConfig } from "@/constants";
import {
  FiatPricingProvider,
  PassportProvider,
  QueryProvider,
  RouteProvider,
} from "@/context";
import { AnalyticsProvider } from "@/context/AnalyticsProvider";
import { AssetImportProvider } from "@/context/AssetImportProvider";
import { BrazeProvider } from "@/context/BrazeProvider";
import { PWAInstallProvider } from "@/context/PWAInstallContext";
import { PreferencesProvider } from "@/context/PreferencesProvider";
import { ProfileDrawerProvider } from "@/context/ProfileDrawerContext";
import theme from "@/styles/theme";
import { ThemeProvider } from "@mui/material/styles";
import ErrorComponent from "./_error";

export type NextPageWithLayout<P = unknown, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

function App(props: AppProps) {
  const { Component, pageProps } = props;
  // @ts-ignore
  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <ErrorBoundary FallbackComponent={ErrorComponent}>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* Primary fallback favicons */}
        <link rel="icon" href="/icons/icon-onLight.svg" type="image/svg+xml" />
        <link
          rel="icon"
          href="/icons/favicon-onLight-16x16.png"
          sizes="16x16"
          type="image/png"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight-32x32.png"
          sizes="32x32"
          type="image/png"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight-48x48.png"
          sizes="48x48"
          type="image/png"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight-96x96.png"
          sizes="96x96"
          type="image/png"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight-180x180.png"
          sizes="180x180"
          type="image/png"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight-512x512.png"
          sizes="512x512"
          type="image/png"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight.ico"
          type="image/x-icon"
        />

        {/* Light mode favicons */}
        <link
          rel="icon"
          href="/icons/icon-onLight.svg"
          type="image/svg+xml"
          media="(prefers-color-scheme: light)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight-16x16.png"
          sizes="16x16"
          type="image/png"
          media="(prefers-color-scheme: light)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight-32x32.png"
          sizes="32x32"
          type="image/png"
          media="(prefers-color-scheme: light)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight-48x48.png"
          sizes="48x48"
          type="image/png"
          media="(prefers-color-scheme: light)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight-96x96.png"
          sizes="96x96"
          type="image/png"
          media="(prefers-color-scheme: light)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight-180x180.png"
          sizes="180x180"
          type="image/png"
          media="(prefers-color-scheme: light)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight-512x512.png"
          sizes="512x512"
          type="image/png"
          media="(prefers-color-scheme: light)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onLight.ico"
          type="image/x-icon"
          media="(prefers-color-scheme: light)"
        />

        {/* Dark mode favicons */}
        <link
          rel="icon"
          href="/icons/icon-onDark.svg"
          type="image/svg+xml"
          media="(prefers-color-scheme: dark)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onDark-16x16.png"
          sizes="16x16"
          type="image/png"
          media="(prefers-color-scheme: dark)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onDark-32x32.png"
          sizes="32x32"
          type="image/png"
          media="(prefers-color-scheme: dark)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onDark-48x48.png"
          sizes="48x48"
          type="image/png"
          media="(prefers-color-scheme: dark)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onDark-96x96.png"
          sizes="96x96"
          type="image/png"
          media="(prefers-color-scheme: dark)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onDark-180x180.png"
          sizes="180x180"
          type="image/png"
          media="(prefers-color-scheme: dark)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onDark-512x512.png"
          sizes="512x512"
          type="image/png"
          media="(prefers-color-scheme: dark)"
        />
        <link
          rel="icon"
          href="/icons/favicon-onDark.ico"
          type="image/x-icon"
          media="(prefers-color-scheme: dark)"
        />

        <link
          rel="apple-touch-icon"
          href="/icons/apple-touch-icon-180x180.png"
          sizes="180x180"
          type="image/png"
        />
        <link rel="manifest" href="/manifest.json" />
        <meta name="apple-mobile-web-app-title" content="Play" />
        <meta name="theme-color" content="#1C1C1C" />
        <meta
          name="msapplication-TileImage"
          content="/icons/ms-tile-144x144.png"
        />
        <meta name="msapplication-TileColor" content="#1C1C1C" />
      </Head>
      <RouteProvider>
        <PWAInstallProvider>
          <AnalyticsProvider>
            <PassportProvider>
              <FeatureFlagProvider>
                <QueryProvider>
                  <PreferencesProvider>
                    <FiatPricingProvider>
                      <AssetImportProvider>
                        <ProfileDrawerProvider>
                          <BrazeProvider>
                            <ThemeProvider theme={theme}>
                              {getLayout(<Component {...pageProps} />)}
                            </ThemeProvider>
                          </BrazeProvider>
                        </ProfileDrawerProvider>

                        <GoogleTagManager gtmId={appConfig.GTM_CONTAINER_ID} />
                        <PrismicPreview repositoryName={repositoryName} />
                      </AssetImportProvider>
                    </FiatPricingProvider>
                  </PreferencesProvider>
                </QueryProvider>
              </FeatureFlagProvider>
            </PassportProvider>
          </AnalyticsProvider>
        </PWAInstallProvider>
      </RouteProvider>
    </ErrorBoundary>
  );
}

export default App;
